import { Link } from "react-router-dom";
import { ArrowRight, Upload, Sparkles, Shirt, Eye, Check } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_ai-garment-try-on/artifacts/bmql0da3_D54CFECA-2253-40CF-9FD6-5BC13F163F92.jpeg";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_ai-garment-try-on/artifacts/skm3z4j3_Photoroom_20251125_232246.jpeg";

export default function LandingPage() {
  const steps = [
    {
      icon: Upload,
      title: "Upload Your Photo",
      description: "Take or upload a photo of yourself. Our AI works best with clear, well-lit images."
    },
    {
      icon: Shirt,
      title: "Select a Design",
      description: "Browse our curated collection of custom garment designs from talented tailors."
    },
    {
      icon: Sparkles,
      title: "AI Magic",
      description: "Our advanced AI places the garment onto your body realistically in seconds."
    },
    {
      icon: Eye,
      title: "Preview & Order",
      description: "See yourself in the design and start your custom order with confidence."
    }
  ];

  const features = [
    "Realistic virtual try-on",
    "Curated designer collection",
    "Instant AI generation",
    "Save your favorites",
    "Direct custom ordering",
    "Money-back guarantee"
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="landing-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 md:pt-40 pb-24 md:pb-32" data-testid="hero-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <div className="animate-in">
              <p className="caption text-[#FF1493] mb-6">Virtual Try-On Technology</p>
              <h1 className="heading-xl mb-6">
                Try Before<br />
                <span className="text-[#FF1493]">You Buy</span>
              </h1>
              <p className="body-lg text-gray-400 mb-8 max-w-lg">
                Experience fashion like never before. Upload your photo, select a design, 
                and watch AI transform you into the perfect outfit.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/signup" data-testid="hero-cta-primary">
                  <button className="btn-primary w-full sm:w-auto">
                    Try It Free
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </Link>
                <Link to="/gallery" data-testid="hero-cta-secondary">
                  <button className="btn-secondary w-full sm:w-auto">
                    Browse Designs
                  </button>
                </Link>
              </div>
              <p className="mt-6 text-sm text-gray-500">
                3 free try-ons. No credit card required.
              </p>
            </div>

            {/* Right Visual */}
            <div className="relative animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <div className="aspect-[4/5] relative overflow-hidden">
                <img
                  src={HERO_IMAGE}
                  alt="TailorGlam Fashion"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              </div>
              {/* Floating Card */}
              <div className="absolute -bottom-6 -left-6 bg-[#141414] border border-white/10 p-6 shadow-float max-w-xs">
                <p className="caption text-[#FF1493] mb-2">AI Powered</p>
                <p className="font-heading text-2xl text-white">Realistic Results</p>
                <p className="text-gray-400 text-sm mt-2">
                  Advanced AI ensures natural-looking virtual try-ons
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-padding bg-[#0f0f0f]" data-testid="how-it-works-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <p className="caption text-[#FF1493] mb-4">The Process</p>
            <h2 className="heading-lg">How It Works</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div 
                key={index} 
                className="relative p-8 bg-[#141414] border border-white/10 hover-lift"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`step-${index + 1}`}
              >
                <div className="absolute -top-4 -left-4 w-8 h-8 bg-[#FF1493] text-white flex items-center justify-center font-mono text-sm">
                  {index + 1}
                </div>
                <step.icon className="w-8 h-8 text-[#FF1493] mb-6" strokeWidth={1.5} />
                <h3 className="font-heading text-xl mb-3 text-white">{step.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="section-padding bg-[#0A0A0A]" data-testid="features-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="caption text-[#FF1493] mb-4">Why TailorGlam</p>
              <h2 className="heading-lg mb-8">
                Fashion Forward,<br />Technology Driven
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3" data-testid={`feature-${index + 1}`}>
                    <div className="w-5 h-5 bg-[#FF1493]/20 flex items-center justify-center">
                      <Check className="w-3 h-3 text-[#FF1493]" />
                    </div>
                    <span className="text-gray-300">{feature}</span>
                  </div>
                ))}
              </div>
              <div className="mt-10">
                <Link to="/studio" data-testid="features-cta">
                  <button className="btn-primary">
                    Start Creating
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_ai-garment-try-on/artifacts/mqn21l4x_IMG_5522.png"
                  alt="TailorGlam Store"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="section-padding bg-[#0f0f0f]" data-testid="pricing-preview-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="caption text-[#FF1493] mb-4">Simple Pricing</p>
              <h2 className="heading-lg mb-6">
                Start Free,<br />Scale When Ready
              </h2>
              <p className="body-lg text-gray-400 mb-8">
                Get 3 free virtual try-ons to experience the magic. 
                Upgrade to Pro for unlimited creativity.
              </p>
              <Link to="/subscription" data-testid="pricing-cta">
                <button className="btn-primary">
                  View Pricing
                  <ArrowRight className="w-4 h-4" />
                </button>
              </Link>
            </div>
            <div className="bg-[#141414] border border-white/10 p-8 md:p-12">
              <p className="caption text-gray-500 mb-4">Pro Plan</p>
              <div className="flex items-baseline gap-2 mb-6">
                <span className="text-5xl font-heading text-white">$12.99</span>
                <span className="text-gray-500">/month</span>
              </div>
              <ul className="space-y-4 mb-8">
                <li className="flex items-center gap-3 text-gray-300">
                  <Check className="w-5 h-5 text-[#FF1493]" />
                  <span>50 AI generations per month</span>
                </li>
                <li className="flex items-center gap-3 text-gray-300">
                  <Check className="w-5 h-5 text-[#FF1493]" />
                  <span>Access to all designs</span>
                </li>
                <li className="flex items-center gap-3 text-gray-300">
                  <Check className="w-5 h-5 text-[#FF1493]" />
                  <span>Priority support</span>
                </li>
                <li className="flex items-center gap-3 text-gray-300">
                  <Check className="w-5 h-5 text-[#FF1493]" />
                  <span>Save unlimited results</span>
                </li>
              </ul>
              <Link to="/signup" data-testid="pricing-signup-cta">
                <button className="btn-primary w-full">
                  Get Started
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#0A0A0A]" data-testid="cta-section">
        <div className="container-custom text-center">
          <h2 className="heading-lg mb-6">
            Ready to Transform<br />Your Shopping Experience?
          </h2>
          <p className="body-lg text-gray-400 mb-10 max-w-2xl mx-auto">
            Join thousands of fashion-forward customers who try before they buy.
          </p>
          <Link to="/signup" data-testid="final-cta">
            <button className="btn-primary">
              Start Your Free Trial
              <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
